/**
 * ImportChartFromSymfoniaModal — Ustawienia → Plan kont, import z Symfonii FK (WebAPI).
 *
 * Wzorzec `AdoptChartTemplateModal.tsx` (Modal + ConfirmDialog + useQuery/useMutation),
 * z dodatkowym krokiem wyniku — import może wygenerować ostrzeżenia (przepięte sieroty,
 * pominięte niepoprawne numery), które user powinien przeczytać, nie tylko zobaczyć toast.
 *
 * Rok obrachunkowy pobierany z `GET /erp-integrations/:id/fk-years` (camelCase, tryb
 * bezpośredni) — domyślnie zaznaczony pierwszy OTWARTY rok. Właściwy import:
 * `POST /erp-integrations/:id/import-chart-of-accounts {companyId, yearId, yearCode}` —
 * `yearId` (numeryczny) to WYŁĄCZNIE parametr wywołania WebAPI Symfonii, `yearCode`
 * (`years[].code`, string — Symfonia bywa alfanumeryczna) to etykieta zapisywana jako
 * `chart_of_accounts.fiscal_year` (migracja 0141). Zawsze zwraca `200` z pełnym wynikiem
 * (backend robi to synchronicznie, nie ma tu pollingu).
 */

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from '@tanstack/react-query';
import { isAxiosError } from 'axios';

import { Button } from '@/components/Button';
import { Card } from '@/components/Card';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { Modal } from '@/components/Modal';
import { Select } from '@/components/Select';
import { Spinner } from '@/components/Spinner';
import { useToast } from '@/contexts/ToastContext';
import { api } from '@/lib/api';

export interface EligibleSymfoniaIntegration {
  id: string;
  name: string;
}

interface FkYear {
  id: number;
  code: string;
  closed: boolean;
  archived: boolean;
}

interface FkYearsResponse {
  years: FkYear[];
}

interface ImportStats {
  fetched: number;
  imported: number;
  skippedExisting: number;
  collapsedSubjectDuplicates: number;
  skippedInvalid: number;
  accountStringMismatch: number;
  orphansReparented: number;
  parentsDemoted: number;
  warningsTotal: number;
}

interface ImportResponse {
  jobId: string;
  outcome: 'ok' | 'partial' | 'no_op';
  stats: ImportStats;
  warnings: string[];
}

interface Props {
  open: boolean;
  companyId: string;
  integrations: EligibleSymfoniaIntegration[];
  onClose: () => void;
  /**
   * Wywołane po pomyślnym imporcie — caller odświeża drzewo planu kont i (zwykle)
   * przełącza widok na `fiscalYear`, żeby user od razu zobaczył zaimportowany plan.
   */
  onImported: (fiscalYear: string) => void;
}

const MAX_VISIBLE_WARNINGS = 20;

export function ImportChartFromSymfoniaModal({
  open,
  companyId,
  integrations,
  onClose,
  onImported,
}: Props) {
  const { t } = useTranslation();
  const toast = useToast();

  const [integrationId, setIntegrationId] = useState<string>(integrations[0]?.id ?? '');
  const [yearId, setYearId] = useState<number | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [result, setResult] = useState<ImportResponse | null>(null);

  // Reset przy każdym otwarciu — lista integracji mogła się zmienić od ostatniego razu.
  useEffect(() => {
    if (open) {
      setIntegrationId(integrations[0]?.id ?? '');
      setYearId(null);
      setResult(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const yearsQ = useQuery({
    queryKey: ['erp-integrations', integrationId, 'fk-years'],
    queryFn: async () => {
      const res = await api.get<FkYearsResponse>(`/erp-integrations/${integrationId}/fk-years`);
      return res.data.years;
    },
    enabled: open && !!integrationId,
    retry: false,
  });

  const years = yearsQ.data ?? [];

  // Domyślnie pierwszy OTWARTY rok — dopiero gdy lata się załadują i user jeszcze nic nie wybrał.
  useEffect(() => {
    if (yearId === null && years.length > 0) {
      setYearId((years.find((y) => !y.closed) ?? years[0])!.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [years]);

  const importMutation = useMutation({
    mutationFn: async () => {
      const yearCode = years.find((y) => y.id === yearId)?.code;
      const res = await api.post<ImportResponse>(
        `/erp-integrations/${integrationId}/import-chart-of-accounts`,
        { companyId, yearId, yearCode },
      );
      return { ...res.data, yearCode };
    },
    onSuccess: (data) => {
      setConfirmOpen(false);
      setResult(data);
      if (data.yearCode) onImported(data.yearCode);
      toast.show({
        variant: data.warnings.length > 0 ? 'info' : 'success',
        title: t('tenant.chartOfAccounts.importSymfonia.toast.success', {
          imported: data.stats.imported,
          skipped: data.stats.skippedExisting,
        }),
      });
    },
    onError: (err) => {
      setConfirmOpen(false);
      const code = isAxiosError(err) ? (err.response?.data as { error?: string })?.error : null;
      const status = isAxiosError(err) ? err.response?.status : null;
      const key =
        status === 402
          ? 'moduleInactive'
          : code === 'NOT_FK'
            ? 'notFk'
            : code === 'AGENT_MODE_UNSUPPORTED'
              ? 'agentMode'
              : code === 'INTEGRATION_DISABLED'
                ? 'integrationDisabled'
                : code === 'APPLICATION_GUID_NOT_SET'
                  ? 'guidNotSet'
                  : code === 'COMPANY_MISMATCH'
                    ? 'companyMismatch'
                    : code === 'IMPORT_CONFLICT'
                      ? 'conflict'
                      : code === 'IMPORT_ALREADY_RUNNING'
                        ? 'alreadyRunning'
                        : code === 'PERMISSION_DENIED'
                          ? 'noPermission'
                          : 'error';
      toast.show({
        variant: 'error',
        title: t(`tenant.chartOfAccounts.importSymfonia.toast.${key}`),
      });
    },
  });

  const selectedYear = years.find((y) => y.id === yearId) ?? null;

  function close() {
    setConfirmOpen(false);
    setResult(null);
    onClose();
  }

  const visibleWarnings = result?.warnings.slice(0, MAX_VISIBLE_WARNINGS) ?? [];
  const hiddenWarningsCount = Math.max(0, (result?.warnings.length ?? 0) - visibleWarnings.length);

  return (
    <>
      <Modal
        open={open && !confirmOpen && result === null}
        onClose={close}
        title={t('tenant.chartOfAccounts.importSymfonia.title')}
        size="md"
      >
        <div data-testid="import-chart-symfonia-modal" className="space-y-4">
          <p className="text-sm text-text-tertiary">
            {t('tenant.chartOfAccounts.importSymfonia.description')}
          </p>

          {integrations.length > 1 && (
            <Select
              label={t('tenant.chartOfAccounts.importSymfonia.integrationLabel')}
              value={integrationId}
              onChange={(e) => {
                setIntegrationId(e.target.value);
                setYearId(null);
              }}
              options={integrations.map((i) => ({ value: i.id, label: i.name }))}
              data-testid="import-chart-symfonia-integration-select"
            />
          )}

          {yearsQ.isLoading ? (
            <div className="flex items-center justify-center py-6">
              <Spinner />
            </div>
          ) : yearsQ.isError ? (
            <Card padding="md">
              <p className="text-sm text-danger-600">
                {t('tenant.chartOfAccounts.importSymfonia.yearsLoadError')}
              </p>
            </Card>
          ) : years.length === 0 ? (
            <Card padding="md">
              <p className="text-sm text-text-tertiary">
                {t('tenant.chartOfAccounts.importSymfonia.noYears')}
              </p>
            </Card>
          ) : (
            <Select
              label={t('tenant.chartOfAccounts.importSymfonia.yearLabel')}
              value={yearId ?? ''}
              onChange={(e) => setYearId(Number(e.target.value))}
              options={years.map((y) => ({
                value: String(y.id),
                label:
                  y.code +
                  (y.closed ? t('tenant.chartOfAccounts.importSymfonia.yearClosedSuffix') : '') +
                  (y.archived ? t('tenant.chartOfAccounts.importSymfonia.yearArchivedSuffix') : ''),
              }))}
              data-testid="import-chart-symfonia-year-select"
            />
          )}

          <div className="flex justify-end">
            <Button
              variant="primary"
              disabled={yearId === null}
              onClick={() => setConfirmOpen(true)}
              data-testid="import-chart-symfonia-submit-btn"
            >
              {t('tenant.chartOfAccounts.importSymfonia.importBtn')}
            </Button>
          </div>
        </div>
      </Modal>

      <ConfirmDialog
        open={confirmOpen}
        variant="primary"
        title={t('tenant.chartOfAccounts.importSymfonia.confirm.title')}
        message={
          selectedYear
            ? t('tenant.chartOfAccounts.importSymfonia.confirm.message', {
                year: selectedYear.code,
              })
            : ''
        }
        confirmLabel={t('tenant.chartOfAccounts.importSymfonia.confirm.confirmLabel')}
        confirming={importMutation.isPending}
        onConfirm={() => importMutation.mutate()}
        onClose={() => setConfirmOpen(false)}
      />

      <Modal
        open={result !== null}
        onClose={close}
        title={t('tenant.chartOfAccounts.importSymfonia.result.title')}
        size="md"
      >
        {result && (
          <div data-testid="import-chart-symfonia-result" className="space-y-3">
            <ul className="space-y-1 text-sm text-text-secondary">
              <li>
                {t('tenant.chartOfAccounts.importSymfonia.result.imported', {
                  count: result.stats.imported,
                })}
              </li>
              <li>
                {t('tenant.chartOfAccounts.importSymfonia.result.skippedExisting', {
                  count: result.stats.skippedExisting,
                })}
              </li>
              {result.stats.collapsedSubjectDuplicates > 0 && (
                <li>
                  {t('tenant.chartOfAccounts.importSymfonia.result.collapsedSubjectDuplicates', {
                    count: result.stats.collapsedSubjectDuplicates,
                  })}
                </li>
              )}
              {result.stats.orphansReparented > 0 && (
                <li>
                  {t('tenant.chartOfAccounts.importSymfonia.result.orphansReparented', {
                    count: result.stats.orphansReparented,
                  })}
                </li>
              )}
              {result.stats.parentsDemoted > 0 && (
                <li>
                  {t('tenant.chartOfAccounts.importSymfonia.result.parentsDemoted', {
                    count: result.stats.parentsDemoted,
                  })}
                </li>
              )}
              {result.stats.skippedInvalid > 0 && (
                <li>
                  {t('tenant.chartOfAccounts.importSymfonia.result.skippedInvalid', {
                    count: result.stats.skippedInvalid,
                  })}
                </li>
              )}
            </ul>

            {visibleWarnings.length > 0 && (
              <Card padding="sm">
                <ul className="space-y-1 text-xs text-text-tertiary">
                  {visibleWarnings.map((w, i) => (
                    <li key={i}>{w}</li>
                  ))}
                  {hiddenWarningsCount > 0 && (
                    <li className="italic">
                      {t('tenant.chartOfAccounts.importSymfonia.result.moreWarnings', {
                        count: hiddenWarningsCount,
                      })}
                    </li>
                  )}
                </ul>
              </Card>
            )}

            <div className="flex justify-end">
              <Button
                variant="secondary"
                onClick={close}
                data-testid="import-chart-symfonia-close-btn"
              >
                {t('tenant.chartOfAccounts.importSymfonia.result.close')}
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </>
  );
}
